import { Select } from "@bfc-software/pallet";
import { Key } from "@react-types/shared";
import clsx from "clsx";
import { ComponentProps, useContext, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
} from "@bfc-software/app-toolbox";
import {
  useTranslation,
  DEFAULT_LOCALE,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import {
  warehouseSwitchTarget,
  WAREHOUSE_FALLBACK_PATH,
} from "@bfc-software/bfc-web-ui/lib/warehouse-switch-target";

interface LocationSelectorProps {
  className?: string;
}

export function LocationSelector({ className }: LocationSelectorProps) {
  const { state } = useContext(AuthContext);
  const { location: locationId, org, lang } = useParams({ strict: false });
  const navigate = useNavigate();

  const orgId = state.status === AUTHENTICATED ? state.orgId : null;
  const locations = useMemo(
    () => (orgId ? AuthUtils.getLocations(state, orgId) : []),
    [state, orgId],
  );

  const { t } = useTranslation({
    lang: lang || DEFAULT_LOCALE,
    namespace: TRANSLATION_NAMESPACE.locationSelector,
  });

  const warehouseOptions: ComponentProps<typeof Select>["items"] = useMemo(
    () =>
      locations.map((entry) => ({
        id: entry.warehouseId,
        label: entry.locationName,
        value: entry.warehouseId,
      })),
    [locations],
  );

  const router = useRouter();
  const matches = useRouterState({ select: (s) => s.matches });

  const targetPath = useMemo(
    () =>
      warehouseSwitchTarget(
        matches[matches.length - 1]?.fullPath,
        (path) => path in router.routesByPath,
      ) as typeof WAREHOUSE_FALLBACK_PATH,
    [matches, router],
  );

  /**
   * Switching warehouse changes the URL, and nothing else.
   *
   * The warehouse layout watches the URL and re-mints the session to match, so
   * this must not also re-mint. Doing both is a loop: one effect moves the URL
   * to the session while the other moves the session to the URL, and each one
   * re-triggers the other, minting a session per pass.
   */
  const handleSelectionChange = (newLocationId: Key | null) => {
    if (typeof newLocationId !== "string" || newLocationId === locationId) {
      return;
    }

    navigate({
      to: targetPath,
      params: {
        org: org ?? "",
        lang: lang ?? "",
        location: newLocationId,
      },
      search: (prev: Record<string, unknown>) => prev,
      replace: true,
    });
  };

  if (locations.length < 1) {
    return <></>;
  }

  return (
    <Select
      display={{ trigger: { minWidth: "200px" } }}
      selectedId={locationId}
      onSelectionChange={handleSelectionChange}
      className={clsx(className)}
      name="warehouse"
      items={warehouseOptions}
      aria-label={t("locationPickerLabel")}
    />
  );
}
